/* eslint-disable no-unused-vars */
import axios, { AxiosInstance } from 'axios';
import { TRACKING_URL } from './connection';
import { getStoredSessionId, getStoredCookieHash } from './api';
import dayjs from 'dayjs';

/**
 * 埋点事件名称枚举
 */
export enum TrackingEventName {
    // repair request 链接点击（侧边栏跳转按钮）
    RepairRequestClick = 'RepairRequestClick',
    // 切换主题
    ThemeToggle = 'ThemeToggle',
    // 语音输入（不管结果和识别内容，就只看点击）
    VoiceInputStart = 'VoiceInputStart',
    // 附件：以上传s3为行为触发
    AttachmentUpload = 'AttachmentUpload'
}

/**
 * 埋点事件数据接口
 */
export interface TrackingEventData {
    [key: string]: any;
}

/**
 * 埋点上报参数
 */
export interface TrackingParams {
    eventName: TrackingEventName | string;
    eventData?: TrackingEventData;
}

/**
 * 生成16位16进制随机请求ID
 */
function generateRequestID(): string {
    return Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

/**
 * 格式化时间戳 YYYY-MM-DD HH:mm:ss
 */
function formatTimestamp(): string {
    return dayjs().format('YYYY-MM-DD HH:mm:ss');
}

/**
 * 埋点服务 axios 实例
 */
const trackingService: AxiosInstance = axios.create({
    baseURL: TRACKING_URL,
    timeout: 10 * 1000,
    headers: {
        'Content-Type': 'application/json'
    }
});

/**
 * 埋点上报方法
 *
 * @param params - 埋点参数
 * @param params.eventName - 事件名称
 * @param params.eventData - 事件具体数据（可选）
 *
 * @example
 * ```ts
 * // 基础用法
 * await trackEvent({ eventName: TrackingEventName.ThemeToggle });
 *
 * // 带事件数据
 * await trackEvent({
 *     eventName: TrackingEventName.ThemeToggle,
 *     eventData: { theme: 'light' }
 * });
 * ```
 */
export const trackEvent = async (params: TrackingParams): Promise<void> => {
    const { eventName, eventData = {} } = params;

    const payload = {
        // 16位的16进制随机数，每次请求自动生成
        requestID: generateRequestID(),
        // 当前sessionId，从 localStorage 自动获取
        sessionID: getStoredSessionId() || '',
        // 用户标识，从 cookie_hash 获取
        userID: getStoredCookieHash() || '',
        // 事件触发时间戳，自动生成
        timeStamp: formatTimestamp(),
        // 事件名称
        eventName,
        // 事件具体数据
        eventData
    };

    try {
        await trackingService.post('/api/v1/telemetry/events', payload);
    } catch (e) {
        // 埋点失败不阻塞主流程，静默处理
        console.error('[埋点上报失败]', eventName, e);
    }
};

/**
 * 批量埋点上报
 *
 * @param events - 埋点事件数组
 */
export const trackBatchEvents = async (events: TrackingParams[]): Promise<void> => {
    const payloads = events.map(({ eventName, eventData = {} }) => ({
        requestID: generateRequestID(),
        sessionID: getStoredSessionId() || '',
        userID: getStoredCookieHash() || '',
        timeStamp: formatTimestamp(),
        eventName,
        eventData
    }));

    try {
        await trackingService.post('/api/v1/air/telemetry/events/batch', { events: payloads });
    } catch (e) {
        console.error('[批量埋点上报失败]', e);
    }
};

export default trackingService;
